import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

function CountdownTimer({ expirationDate }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiration = new Date(expirationDate).getTime();
      const difference = expiration - now;

      if (difference <= 0) {
        return "Available now";
      }

      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      // Pad numbers with zeros for consistent width
      const paddedHours = hours.toString().padStart(2, "0");
      const paddedMinutes = minutes.toString().padStart(2, "0");
      const paddedSeconds = seconds.toString().padStart(2, "0");

      return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [expirationDate]);

  return <div className="font-mono text-xl text-accent-coral">{timeLeft}</div>;
}

export default function ClaimSpace({ spaceSlug, error }) {
  const router = useRouter();

  // Determine the type of claim based on the error status code
  const isDraftClaim = error?.status === 423;
  const isLocked = error?.status === 425;
  const isMissedOpportunity = error?.status === 410;
  const isDisallowedSlug =
    error?.status === 403 &&
    error?.message === "This URL is not available for use";
  const isNotFound = error?.status === 404 || !error?.status;

  const [newSlug, setNewSlug] = useState("");
  const [isSlugValid, setIsSlugValid] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [slugError, setSlugError] = useState("");

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setNewSlug(value);

    if (value.length < 3) {
      setIsSlugValid(false);
      setSlugError(value ? "Slug must be at least 3 characters" : "");
    } else {
      checkSlugAvailability(value);
    }
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) return;

    setIsChecking(true);
    setSlugError("");

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/check-slug?slug=${slug}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setIsSlugValid(data.available);

      if (!data.available) {
        setSlugError(data.message || "This slug is not available");
      }
    } catch (error) {
      console.error("Error checking slug:", error);
      setIsSlugValid(false);
      setSlugError("Unable to check availability");
    } finally {
      setIsChecking(false);
    }
  };

  const handleClaimNewSlug = () => {
    if (!isSlugValid) return;

    if (typeof window !== "undefined" && window.umami) {
      window.umami.track("space_claim_new_slug", {
        source: "missed_opportunity_page",
        old_slug: spaceSlug,
        new_slug: newSlug,
      });
    }

    router.push(`/onboarding?slug=${newSlug}`);
  };

  const handleClaimClick = () => {
    if (typeof window !== "undefined" && window.umami) {
      window.umami.track("space_claim_referral", {
        source: "claim_page",
        slug: spaceSlug,
        type: isLocked
          ? "locked_handle"
          : isDraftClaim
            ? "draft_claim"
            : "regular_claim",
      });
    }
    router.push(`/onboarding?slug=${spaceSlug}`);
  };

  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center p-4 text-center ${
        isMissedOpportunity
          ? "bg-gradient-to-b from-red-700 to-gray-900"
          : isLocked
            ? "bg-gradient-to-b from-gray-700 to-gray-900"
            : isDraftClaim
              ? "bg-gradient-to-b from-rose-600 to-orange-600"
              : isDisallowedSlug
                ? "bg-gradient-to-b from-red-800 to-gray-900"
                : "bg-gradient-to-b from-indigo-600 to-purple-700"
      }`}
    >
      <Image
        src="/logos/beta/white.svg"
        alt="Logo"
        width={180}
        height={60}
        className="mb-8"
      />
      <h1 className="text-4xl font-bold mb-4 text-white">
        {isMissedOpportunity
          ? "Missed Opportunity"
          : isLocked
            ? "Handle Currently Locked"
            : isDraftClaim
              ? "Act Fast - Claim This Space!"
              : isDisallowedSlug
                ? "Reserved Handle"
                : "Claim Your Space"}
      </h1>

      <div className="bg-white/10 rounded-lg px-8 py-4 mb-8 backdrop-blur-sm">
        <p className="text-3xl font-mono text-white">
          qensa.me/
          <span
            className={
              isMissedOpportunity || isDisallowedSlug
                ? "text-red-400"
                : "text-emerald-400"
            }
          >
            {spaceSlug}
          </span>
        </p>
      </div>

      {isDisallowedSlug && (
        <div className="bg-white/20 rounded-lg p-6 mb-6 max-w-lg backdrop-blur-sm">
          <p className="text-white font-semibold mb-4">
            ⚠️ This handle is reserved and not available for use
          </p>

          <div className="mb-4">
            <p className="text-white/80 text-sm mb-2">
              Choose a different handle:
            </p>
            <div className="flex items-center">
              <span className="text-white/70 mr-2">qensa.me/</span>
              <input
                type="text"
                value={newSlug}
                onChange={handleSlugChange}
                className="flex-1 bg-white/20 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                placeholder="your-new-handle"
                minLength={3}
                pattern="[a-z0-9-]+"
              />
            </div>
            {slugError && (
              <p className="text-red-300 text-sm mt-1">{slugError}</p>
            )}
            {!slugError && newSlug.length >= 3 && isSlugValid && (
              <p className="text-green-300 text-sm mt-1">
                ✓ This handle is available
              </p>
            )}
          </div>

          <button
            onClick={handleClaimNewSlug}
            disabled={!isSlugValid || isChecking}
            className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 
              ${
                isSlugValid && !isChecking
                  ? "bg-white text-black hover:bg-white/90"
                  : "bg-white/50 text-gray-800 cursor-not-allowed"
              }`}
          >
            {isChecking ? "Checking availability..." : "Claim This New Handle"}
          </button>
        </div>
      )}

      {(isDraftClaim || isLocked) && (
        <div
          className={`bg-white/20 rounded-lg p-4 mb-6 max-w-lg backdrop-blur-sm ${
            isLocked ? "" : "animate-pulse"
          }`}
        >
          <p className="text-white font-semibold">
            {isLocked
              ? "This handle is temporarily locked"
              : "⚡️ Someone started drafting this handle but hasn't published yet"}
          </p>
          {isLocked && error?.expiration_at && (
            <div className="mt-2">
              <p className="text-white/80 text-sm mb-1">Available in:</p>
              <CountdownTimer expirationDate={error.expiration_at} />
            </div>
          )}
        </div>
      )}

      {isMissedOpportunity && (
        <div className="bg-white/20 rounded-lg p-6 mb-6 max-w-lg backdrop-blur-sm">
          <p className="text-white font-semibold mb-4">
            ⚠️ Unfortunately, this handle has been claimed by someone else
          </p>

          <div className="mb-4">
            <p className="text-white/80 text-sm mb-2">
              Choose a different handle:
            </p>
            <div className="flex items-center">
              <span className="text-white/70 mr-2">qensa.me/</span>
              <input
                type="text"
                value={newSlug}
                onChange={handleSlugChange}
                className="flex-1 bg-white/20 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                placeholder="your-new-handle"
                minLength={3}
                pattern="[a-z0-9-]+"
              />
            </div>
            {slugError && (
              <p className="text-red-300 text-sm mt-1">{slugError}</p>
            )}
            {!slugError && newSlug.length >= 3 && isSlugValid && (
              <p className="text-green-300 text-sm mt-1">
                ✓ This handle is available
              </p>
            )}
          </div>

          <button
            onClick={handleClaimNewSlug}
            disabled={!isSlugValid || isChecking}
            className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 
              ${
                isSlugValid && !isChecking
                  ? "bg-white text-black hover:bg-white/90"
                  : "bg-white/50 text-gray-800 cursor-not-allowed"
              }`}
          >
            {isChecking ? "Checking availability..." : "Claim This New Handle"}
          </button>
        </div>
      )}

      <p className="text-xl text-white/80 mb-6 max-w-lg">
        {error?.message ||
          "This space is available! Create your own personalized space and start sharing your content today."}
      </p>

      {!isMissedOpportunity && !isDisallowedSlug && (
        <div className="space-y-6">
          <div className="flex flex-col items-center space-y-2">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mb-2 ${
                isLocked
                  ? "bg-white/20"
                  : isDraftClaim
                    ? "bg-white/30"
                    : "bg-white/20"
              }`}
            >
              {isLocked ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              ) : isDraftClaim ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              )}
            </div>
            <h3 className="text-xl font-semibold text-white">
              {isLocked
                ? "Temporary Lock"
                : isDraftClaim
                  ? "Limited Time Opportunity"
                  : "Quick Setup"}
            </h3>
            <p className="text-white/80">
              {isLocked
                ? "Check back when the timer expires"
                : isDraftClaim
                  ? "Claim it now before it's published by someone else"
                  : "Get your space up and running in minutes"}
            </p>
          </div>

          {isLocked ? (
            <button
              disabled
              className="inline-block px-8 py-4 rounded-lg font-semibold bg-white/50 text-white cursor-not-allowed"
            >
              Currently Locked
            </button>
          ) : (
            <button
              onClick={handleClaimClick}
              className={`inline-block px-8 py-4 rounded-lg font-semibold transition-all duration-300 ${
                isDraftClaim
                  ? "bg-white text-rose-600 hover:bg-white/90 hover:scale-105 transform"
                  : "bg-white text-black hover:bg-white/90"
              }`}
            >
              {isDraftClaim ? "Claim Now" : "Claim This Space"}
            </button>
          )}

          <p className="text-white/60 text-sm">
            Already have an account?{" "}
            <Link
              href="/signin"
              className="text-white underline hover:text-white/90"
            >
              Sign in
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}
